import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from 'components/system/layouts/Layout';
import ScreenContext from 'Contexts/Common/ScreenContext';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Pagination } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import redirectLinks from 'constants/redirectLinks';
import COMMON from 'constants/common';
import MachineConfContentSettingDetail from 'components/machineConf/organisms/contents/MachineConfContentSettingDetail';

import { useAppDispatch, useAppSelector } from 'redux/hooks';
import {
  ConfListType,
  MachineConfDuoType,
  operationConfDuoType,
  areaConfDuoType,
  pitConfDuoType,
} from 'types/machineConf/machineConfType';
import NormalButton from 'components/system/atoms/buttons/NormalButton';
import backAllowIcon from 'assets/images/backAllowIcon.svg';
import { setMachineDuoConf, setSelectedStationInfo, setSelectFlg } from 'redux/slices/machineConfSlice';
import { SaveDetailInfoType } from 'types/machineConf/stationSelectType';
import HttpConnection from 'utils/httpConnection';
import { REST_API } from 'constants/apiUrls';

import { getClassificationName } from 'utils/common.helper';
import CLSFCN from 'constants/classification';
import ConfSaveDialog from 'components/machineConf/atoms/dialog/ConfSaveDialog';
import { InfoDialog } from 'components/system/organisms';
import { InfoDialogProps } from 'components/system/organisms/dialogs/InfoDialog';
import MESSAGES from 'constants/messages';
import MsgHelper from 'utils/message.helper';
import { formatDate } from 'utils/datetime.helper';

const CONF_NAME_MAX = 50;
const MSG301: string = MsgHelper.messageFormat(MESSAGES.INF301, '設定');

/* ************ Context ************ */
const ScreenContextValue = {
  title: 'ステーション設定詳細画面',
  screenId: COMMON.SCREEN_ID.STATION_HOME_DETAIL_DUO,
  currentMenu: COMMON.MENU.MACHINE_CONF,
  disableContents: true,
};

type StationHomeProps = {
  stationName: string;
  giftName: string;
  machineType: string | null;
  machineVersion: string | null;
  position: string | null | undefined;
};

type StyleProps = {
  leftRight?: string;
};

/* ************ Style ************ */
const useStyles = makeStyles()(() => ({
  breakText: {
    whiteSpace: 'break-spaces',
    marginLeft: '5px',
  },
  returnButton: {
    width: '157px',
    height: '70px',
    background: '#EBEBEB',
    margin: '0 0 1px auto',
    fontSize: '20px',
    boxShadow: '5px 5px 3px rgba(0, 0, 0, 0.2)',
    padding: 0,
    textAlign: 'left',
    lineHeight: '24px',
    justifyContent: 'flex-start',
  },
  backIconWrap: { marginLeft: '3px' },
  root: {
    display: 'flex',
    minWidth: '1080px',
    minHeight: '563px',
    margin: '15px 4px',
  },
  buttonArea: {
    display: 'grid',
    gap: '10px',
    width: '164px',
    height: '154px',
  },
  button: {
    width: '157px',
    height: '70px',
    background: '#EBEBEB',
    margin: '0 0 1px auto',
    fontSize: '20px',
    boxShadow: '5px 5px 3px rgba(0, 0, 0, 0.2)',
    textAlign: 'left',
    lineHeight: '24px',
    justifyContent: 'flex-start',
    padding: '6px 8px',
  },

  mainArea: {
    margin: '0 20px 0 auto',
  },
  description: {
    fontSize: '23px',
    padding: '34px 94px',
    height: '94px',
  },
  middleContent: {
    display: 'flex',
    margin: '0 0 0 14px',
    height: '540px',
    '& .contentArea': {
      display: 'flex',
      width: '918px',
    },
  },
  table: {
    width: '100%',
    '& th,td': {
      border: '1px solid #707070',
      fontSize: '23px',
      minHeight: '74px',
    },
    '& th': {
      textAlign: 'center',
      padding: 0,
    },
    '& tbody tr': {},
    '& td': {
      padding: '1px 6px',
      '& .cellStyle': {
        fontSize: '16px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        marginTop: 15,
      },
    },
    '& td.bgGray': {
      backgroundColor: '#EEEEEE',
    },
  },
  grayCover: {
    position: 'absolute',
    top: 0,
    left: 0,
    backgroundColor: 'rgba(128, 128, 128, 0.5)',
    zIndex: 100,
  },
  grayCoverDark: {
    position: 'absolute',
    top: 0,
    left: 0,
    backgroundColor: 'rgba(128, 128, 128, 1)',
    zIndex: 100,
  },
  marginX: {
    margin: '0 4px',
    textAlign: 'center',
  },
  marginRight: {
    margin: '0 4px 0 0',
    textAlign: 'center',
  },
  marginLeft: {
    margin: '0 0 0 4px',
    textAlign: 'center',
  },
  fallingDirection: {
    width: '144px',
    height: '84px',
    justifyItems: 'center',
    '& .arrow': {
      margin: '0 4px',
      textAlign: 'center',
      fontSize: '20px',
      fontWeight: 'bold',
      fontFamily: 'sans-serif',
    },
    '& .colorGray': {
      color: 'lightgray',
    },
  },
}));

const StationHomeDetailDuo: React.FC<React.PropsWithChildren<unknown>> = () => {
  const navigate = useNavigate();
  const { selectConfId } = useParams<{ selectConfId: string }>();
  const dispatch = useAppDispatch();
  const { machineDuoConf, selectedStation, operationFlg, checkWay } = useAppSelector((state) => state.machineConf);

  const { stationList } = useAppSelector((state) => state.machineConf.stationSelect);
  const [isStationListScreen, setIsStationListScreen] = React.useState(false);
  const [textButtonBack, setTextButtonBack] = React.useState('ステーション\n一覧');
  const savedConfList: Array<ConfListType> = useAppSelector((state) => state.machineConf.savedConfList);
  const { conf, operationConfDuo, areaConfDuo, pitConfDuo, partitionOtherConfDuo, ledSoundConfDuo } = machineDuoConf;
  const leftRight = useAppSelector((state) => state.machineConf.selectedStation.leftRight);
  const { classes } = useStyles();
  const isLeft = leftRight === COMMON.LEFT_RIGHT.LEFT;
  const isStationConf = conf?.confDiv === COMMON.CONF_DIV.STATION_CONF;
  const [saveConfName, setSaveConfName] = React.useState('');
  const [stationHomeProps, setStationHomeProps] = React.useState<StationHomeProps>({
    stationName: '',
    giftName: '',
    machineType: null,
    machineVersion: null,
    position: null,
  });
  const [savedConfProps, setSavedConfProps] = React.useState<SaveDetailInfoType>({
    saveName: '',
    saveMachineVersion: null,
    saveMachineType: null,
  });

  const processing = React.useRef(false);
  const [infoDialog, setInfoDialog] = React.useState<InfoDialogProps>({
    open: false,
    msg: '',
  });
  const [infoDialogSaveConf, setInfoDialogSaveConf] = React.useState({
    open: false,
    msg: '',
  });
  const [openSaveConfInfoDialog, setOpenSaveConfInfoDialog] = React.useState(false);

  const [openConfSaveDialog, setOpenConfSaveDialog] = React.useState(false);

  const [page, setPage] = React.useState(1);

  // 選択したステーションの設定情報を取得
  const getSettingDetail = async (confId: number) => {
    if (confId) {
      const http = new HttpConnection({ dispatch });
      const data = await http.post<MachineConfDuoType>(REST_API.MACHINE_CONF.GET_SETTING_DETAIL_DUO, { confId });

      // 選択ステーションと取得データの左右区分が一致しない場合、左右を反転
      if (leftRight !== data.conf?.leftRight) {
        /**
         * 9文字（'0'/'1'）を [0,1,2,3,4,5,6,7,8] → [0,7,6,5,4,3,2,1,8] に並び替える
         * 入力が不正な場合はそのまま返却
         */
        const reorderGiftFallingDirection9 = (s: string): string => {
          if (typeof s !== 'string' || s.length !== 9 || /[^01]/.test(s)) {
            return s;
          }
          const oldArr = s.split('');
          const order = [0, 7, 6, 5, 4, 3, 2, 1, 8];
          const newArr = order.map((i) => oldArr[i]);
          return newArr.join('');
        };

        const tempOperationConfDuo: operationConfDuoType = { ...data.operationConfDuo! };
        const tempAreaConfDuo: areaConfDuoType = { ...data.areaConfDuo! };
        const tempPitConfDuo: pitConfDuoType = { ...data.pitConfDuo! };

        // 左右を反転して格納する

        [tempOperationConfDuo.detailGrabLPower, tempOperationConfDuo.detailGrabRPower] = [
          tempOperationConfDuo.detailGrabRPower,
          tempOperationConfDuo.detailGrabLPower,
        ];

        [tempAreaConfDuo.detailArea1GrabLPower, tempAreaConfDuo.detailArea1GrabRPower] = [
          tempAreaConfDuo.detailArea1GrabRPower,
          tempAreaConfDuo.detailArea1GrabLPower,
        ];

        [tempAreaConfDuo.detailArea1LiftupLPower, tempAreaConfDuo.detailArea1LiftupRPower] = [
          tempAreaConfDuo.detailArea1LiftupRPower,
          tempAreaConfDuo.detailArea1LiftupLPower,
        ];

        [tempAreaConfDuo.detailArea2GrabLPower, tempAreaConfDuo.detailArea2GrabRPower] = [
          tempAreaConfDuo.detailArea2GrabRPower,
          tempAreaConfDuo.detailArea2GrabLPower,
        ];

        [tempAreaConfDuo.detailArea2LiftupLPower, tempAreaConfDuo.detailArea2LiftupRPower] = [
          tempAreaConfDuo.detailArea2LiftupRPower,
          tempAreaConfDuo.detailArea2LiftupLPower,
        ];

        tempPitConfDuo.simpleGiftFallingDirection = reorderGiftFallingDirection9(
          tempPitConfDuo.simpleGiftFallingDirection,
        );

        tempPitConfDuo.detailArea1GiftFallingDirection = reorderGiftFallingDirection9(
          tempPitConfDuo.detailArea1GiftFallingDirection,
        );

        tempPitConfDuo.detailArea2GiftFallingDirection = reorderGiftFallingDirection9(
          tempPitConfDuo.detailArea2GiftFallingDirection,
        );

        data.operationConfDuo = tempOperationConfDuo;
        data.areaConfDuo = tempAreaConfDuo;
        data.pitConfDuo = tempPitConfDuo;
      }

      dispatch(setMachineDuoConf(data));
    }
  };

  // 選択中のステーション情報取得
  const getStationSelected = (configId: number) => {
    if (configId) {
      const station = stationList.find((item) => item.confId === configId);
      if (station) {
        setStationHomeProps({
          giftName: station.giftName ?? COMMON.BLANK,
          stationName: station.stationName ?? COMMON.BLANK,
          machineType: station.machineType,
          machineVersion: station.machineVersion,
          position: station.position,
        });
        if (isStationConf) {
          dispatch(
            setSelectedStationInfo({
              stationName: station.stationName ?? '',
              giftName: station.giftName ?? '',
              leftRight: station.leftRight,
              boardStation: station.boardStation,
              machineType: station.machineType,
              machineVersion: station.machineVersion,
              confId: Number(selectConfId),
              position: station.position,
            }),
          );
        }
      }
    } else {
      setStationHomeProps({
        giftName: selectedStation.giftName ?? COMMON.BLANK,
        stationName: selectedStation.stationName ?? COMMON.BLANK,
        machineType: selectedStation.machineType,
        machineVersion: selectedStation.machineVersion,
        position: selectedStation.position,
      });
    }
  };

  // 選択中の保存設定情報取得
  const getSavedConfInfo = (configId: number) => {
    if (configId) {
      const savedConfInfo = savedConfList.find((item) => Number(item.value) === configId);
      if (savedConfInfo) {
        setSavedConfProps({
          saveName: savedConfInfo.name,
          saveMachineVersion: savedConfInfo.machineVersion,
          saveMachineType: savedConfInfo.machineType,
        });
      }
    } else {
      setSavedConfProps({
        saveName: conf?.saveConfName ?? '',
        saveMachineVersion: conf?.machineVersion ?? null,
        saveMachineType: conf?.machineType ?? null,
      });
    }
  };

  useEffect(() => {
    setIsStationListScreen(isStationConf);
    if (isStationConf) {
      setTextButtonBack('ステーション\n一覧');
    } else {
      setTextButtonBack('保存設定\n一覧');
    }
  }, [isStationConf, selectConfId]);

  useEffect(() => {
    const confId = Number(selectConfId);
    getStationSelected(confId);
    getSettingDetail(confId);
    getSavedConfInfo(confId);
  }, [selectConfId, stationList]);

  // 戻るボタン
  const handleClickReturn = () => {
    // state(operationFlg,checkWay)の状況を初期化する
    dispatch(setSelectFlg({ operationFlg: '0', checkWay: '1', isSelectAllSetting: '1' }));
    navigate(isStationConf ? redirectLinks.MCONF_STATION_SELECT : redirectLinks.MCONF_STATION_CONF_SAVED_CONF);
  };

  // サウンド/演出ボタン
  const handleClickSound = () => {
    navigate(redirectLinks.MCONF_DUO_CONF_LED_SOUND, {
      state: isSaveSetting ? savedConfProps : undefined,
    });
  };

  // 共通ボタン
  const handleClickCommon = () => {
    navigate(redirectLinks.MCONF_DUO_CONF_COMMON);
  };

  const handleOpenSaveConfirmDialog = () => setOpenConfSaveDialog(true);
  const handleCloseConfSaveDialog = (): void => {
    setOpenConfSaveDialog(false);
  };

  // 設定を保存押下
  const handleOnClickOk = async (e: React.FormEvent) => {
    e.preventDefault();

    // 二重登録防止
    if (processing.current) return;
    processing.current = true;

    if (!saveConfName) {
      setInfoDialog({
        open: true,
        msg: MsgHelper.messageFormat(MESSAGES.INF103, 'タイトル'),
        closeFunc: () => setInfoDialog({ open: false, msg: '' }),
      });
    } else if (saveConfName === 'invalid') {
      setInfoDialog({
        open: true,
        msg: MsgHelper.messageFormat(MESSAGES.INF105),
        closeFunc: () => setInfoDialog({ open: false, msg: '' }),
      });
    } else if (CONF_NAME_MAX < saveConfName.length) {
      // 文字数チェック
      setInfoDialog({
        open: true,
        msg: MsgHelper.messageFormat(MESSAGES.INF111, CONF_NAME_MAX.toString()),
        closeFunc: () => setInfoDialog({ open: false, msg: '' }),
      });
    } else {
      const confId = selectConfId ? Number(selectConfId) : selectedStation.confId;
      const station = stationList.find((item) => item.confId === confId);

      const resultSaveConf = await saveConfDuo({
        saveConfName,
        machineConf: machineDuoConf,
        leftRight: station?.leftRight ?? '',
        boardStation: station?.boardStation ?? '',
        machineType: station?.machineType ?? '',
        machineVersion: station?.machineVersion ?? '',
      });
      if (resultSaveConf && typeof resultSaveConf !== 'number' && !resultSaveConf.success) {
        setInfoDialogSaveConf({
          open: true,
          msg: resultSaveConf.errors[0][0],
        });
      } else {
        setOpenConfSaveDialog(false);
        setOpenSaveConfInfoDialog(true);
        setSaveConfName('');
      }
    }
    // 二重登録防止
    setTimeout(() => {
      // 処理中フラグを下げる
      processing.current = false;
    }, 2000);
  };

  const saveConfDuo = async (dataRequest: {
    saveConfName: string;
    machineConf: MachineConfDuoType;
    leftRight: string;
    boardStation: string;
    machineType: string | null;
    machineVersion: string | null;
  }) => {
    const http = new HttpConnection({ dispatch });
    const result = await http.postSecond(REST_API.MACHINE_CONF.SAVE_CONF_DUO, dataRequest);
    return result;
  };

  // convert to half using normalize
  function convertToHalf(value: string) {
    return value.normalize('NFKC');
  }

  // 設定保存タイトルの変更
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = convertToHalf(e.target.value);
    const checkSpace = value.trim();
    const spaceLength = value.length;

    if (checkSpace === '' && spaceLength !== 0) {
      setSaveConfName('invalid');
    } else {
      setSaveConfName(e.target.value);
    }
  };

  // ダイアログを閉じる
  const handleCloseInfoDialog = (): void => {
    setOpenSaveConfInfoDialog(false);
    setInfoDialogSaveConf({ open: false, msg: '' });
  };

  const handleClickSaveConf = React.useCallback(() => {
    navigate(redirectLinks.MCONF_STATION_CONF_SAVED_CONF);
  }, []);

  const handleChangePage = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  // 保存設定一覧画面から遷移の場合true
  const isSaveSetting = machineDuoConf?.conf?.confDiv === COMMON.CONF_DIV.SAVE_CONF;
  // 一部設定確認時の場合true
  const isPartialCheckActive = machineDuoConf?.conf?.confDiv === COMMON.CONF_DIV.SAVE_CONF && checkWay === '0';

  return (
    <ScreenContext.Provider value={ScreenContextValue}>
      <Layout>
        <MachineConfContentSettingDetail
          isStationListScreen={isStationListScreen}
          isSaveSetting={isSaveSetting}
          isLeft={isLeft}
          stationName={stationHomeProps.stationName}
          giftName={stationHomeProps.giftName}
          machineType={isStationConf ? stationHomeProps.machineType : savedConfProps.saveMachineType}
          machineVersion={stationHomeProps.machineVersion}
          savedConfProps={savedConfProps}
          position={stationHomeProps.position}>
          {/* <MachineConfContent savedConfProps={savedConfProps}> */}
          <Box className={classes.root}>
            <Box className={classes.buttonArea}>
              <NormalButton className={classes.returnButton} onClick={handleClickReturn}>
                <img src={backAllowIcon} alt="戻る" width={28} height={28} className={classes.backIconWrap} />
                <p className={classes.breakText}>{`${textButtonBack}`}</p>
              </NormalButton>
              {isPartialCheckActive ? (
                <NormalButton className={classes.button} onClick={handleClickSound} disabled>
                  サウンド/演出
                </NormalButton>
              ) : (
                <NormalButton className={classes.button} onClick={handleClickSound}>
                  サウンド/演出
                </NormalButton>
              )}
              {!isSaveSetting && (
                <NormalButton className={classes.button} onClick={handleClickCommon}>
                  共通
                </NormalButton>
              )}
              {!isSaveSetting && (
                <NormalButton className={classes.button} onClick={handleOpenSaveConfirmDialog}>
                  設定を保存
                </NormalButton>
              )}
              {!isSaveSetting && (
                <NormalButton className={classes.button} onClick={handleClickSaveConf}>
                  保存設定一覧
                </NormalButton>
              )}
            </Box>

            <Box className={classes.mainArea}>
              <Box className={classes.middleContent}>
                {page === 1 && (
                  <TablePage1 machineDuoConf={machineDuoConf} isPartialCheckActive={isPartialCheckActive}/>
                )}
                {page === 2 && (
                  <TablePage2 machineDuoConf={machineDuoConf} isPartialCheckActive={isPartialCheckActive}/>
                )}
                {page === 3 && (
                  <TablePage3 machineDuoConf={machineDuoConf} isPartialCheckActive={isPartialCheckActive} />
                )}
                {page === 4 && (
                  <TablePage4 machineDuoConf={machineDuoConf} isPartialCheckActive={isPartialCheckActive} />
                )}
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                <Pagination
                  count={4}
                  page={page}
                  onChange={handleChangePage}
                  variant="outlined"
                  shape="rounded"
                  sx={{
                    // すべてのアイテムのデフォルト背景（＝未選択）
                    '& .MuiButtonBase-root': {
                      backgroundColor: '#fff',
                    },
                    '& .Mui-selected': {
                      backgroundColor: 'rgba(0, 0, 0, 0.08)',
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>
          {/* </MachineConfContent> */}
        </MachineConfContentSettingDetail>
      </Layout>
      <ConfSaveDialog
        open={openConfSaveDialog}
        onOk={handleOnClickOk}
        handleChange={handleChange}
        onCancel={handleCloseConfSaveDialog}
      />
      <InfoDialog open={infoDialog.open} msg={infoDialog.msg} closeFunc={infoDialog.closeFunc} />
      <InfoDialog open={infoDialogSaveConf.open} msg={infoDialogSaveConf.msg} closeFunc={handleCloseInfoDialog} />
      <InfoDialog open={openSaveConfInfoDialog} msg={MSG301} closeFunc={handleCloseInfoDialog} />
    </ScreenContext.Provider>
  );
};

type TablePageProps = {
  machineDuoConf: MachineConfDuoType;
  isPartialCheckActive?: boolean;
};

// 機器設定１ページ目
const TablePage1: React.FC<React.PropsWithChildren<TablePageProps>> = ({ machineDuoConf, isPartialCheckActive }) => {
  const { classes } = useStyles();
  const { operationConfDuo, areaConfDuo, pitConfDuo, partitionOtherConfDuo, ledSoundConfDuo, conf } = machineDuoConf;
  const headerWidth = ['224px', '312px', '382px'];

  // 表示するステーションが左か右かの判定用変数宣言
  const leftRight = useAppSelector((state) => state.machineConf.selectedStation.leftRight);
  const isLeft = leftRight === COMMON.LEFT_RIGHT.LEFT;

  /*
  くわしい設定のアームパワー分割エリアの表示位置を左右STで切り替える処理
  左STの機器設定を表示する場合、アームパワーの"くわしい"設定の左側には「エリア1」のアームパワーを、右側には「エリア2」のアームパワーが表示されるように変数値をセットする。
  右STの機器設定を表示する場合、アームパワーの"くわしい"設定の左側には「エリア2」のアームパワーを、右側には「エリア1」のアームパワーが表示されるように変数値をセットする。
  */
  // 上昇時アームパワー 左側のエリア表示
  const detailLiftupLPowerLeftside = isLeft ? areaConfDuo?.detailArea1LiftupLPower : areaConfDuo?.detailArea2LiftupLPower;
  const detailLiftupCPowerLeftside = isLeft ? areaConfDuo?.detailArea1LiftupCPower : areaConfDuo?.detailArea2LiftupCPower;
  const detailLiftupRPowerLeftside = isLeft ? areaConfDuo?.detailArea1LiftupRPower : areaConfDuo?.detailArea2LiftupRPower;
  // 上昇時アームパワー 右側のエリア表示
  const detailLiftupLPowerRightside = isLeft ? areaConfDuo?.detailArea2LiftupLPower : areaConfDuo?.detailArea1LiftupLPower;
  const detailLiftupCPowerRightside = isLeft ? areaConfDuo?.detailArea2LiftupCPower : areaConfDuo?.detailArea1LiftupCPower;
  const detailLiftupRPowerRightside = isLeft ? areaConfDuo?.detailArea2LiftupRPower : areaConfDuo?.detailArea1LiftupRPower;
  // つかみ時アームパワー 左側のエリア表示
  const detailGrabLPowerLeftside = isLeft ? areaConfDuo?.detailArea1GrabLPower : areaConfDuo?.detailArea2GrabLPower;
  const detailGrabCPowerLeftside = isLeft ? areaConfDuo?.detailArea1GrabCPower : areaConfDuo?.detailArea2GrabCPower;
  const detailGrabRPowerLeftside = isLeft ? areaConfDuo?.detailArea1GrabRPower : areaConfDuo?.detailArea2GrabRPower;
  // つかみ時アームパワー 右側のエリア表示
  const detailGrabLPowerRightside = isLeft ? areaConfDuo?.detailArea2GrabLPower : areaConfDuo?.detailArea1GrabLPower;
  const detailGrabCPowerRightside = isLeft ? areaConfDuo?.detailArea2GrabCPower : areaConfDuo?.detailArea1GrabCPower;
  const detailGrabRPowerRightside = isLeft ? areaConfDuo?.detailArea2GrabRPower : areaConfDuo?.detailArea1GrabRPower;

  /*
  くわしい設定のタイミング分割エリアの表示位置を左右STで切り替える処理
  左STの機器設定を表示する場合、タイミングの"くわしい"設定の左側には「エリア1」のタイミングを、右側には「エリア2」のタイミングが表示されるように変数値をセットする。
  右STの機器設定を表示する場合、タイミングの"くわしい"設定の左側には「エリア2」のタイミングを、右側には「エリア1」のタイミングが表示されるように変数値をセットする。
  */
  // 左側の分割エリア表示用定数
  const detailMovingLeftside = isLeft ? areaConfDuo?.detailArea1Moving : areaConfDuo?.detailArea2Moving;  // 横移動時
  const detailUp3Leftside = isLeft ? areaConfDuo?.detailArea1Up3 : areaConfDuo?.detailArea2Up3;  // 上昇3
  const detailUp2Leftside = isLeft ? areaConfDuo?.detailArea1Up2 : areaConfDuo?.detailArea2Up2;  // 上昇2
  const detailUp1Leftside = isLeft ? areaConfDuo?.detailArea1Up1 : areaConfDuo?.detailArea2Up1;  // 上昇1
  const detailImmediateLeftside = isLeft ? areaConfDuo?.detailArea1Immediate : areaConfDuo?.detailArea2Immediate;  // 上昇直後
  // 右側の分割エリア表示用定数
  const detailMovingRightside = isLeft ? areaConfDuo?.detailArea2Moving : areaConfDuo?.detailArea1Moving;  // 横移動時
  const detailUp3Rightside = isLeft ? areaConfDuo?.detailArea2Up3 : areaConfDuo?.detailArea1Up3;  // 上昇3
  const detailUp2Rightside = isLeft ? areaConfDuo?.detailArea2Up2 : areaConfDuo?.detailArea1Up2;  // 上昇2
  const detailUp1Rightside = isLeft ? areaConfDuo?.detailArea2Up1 : areaConfDuo?.detailArea1Up1;  // 上昇1
  const detailImmediateRightside = isLeft ? areaConfDuo?.detailArea2Immediate : areaConfDuo?.detailArea1Immediate;  // 上昇直後

  /*
  くわしい設定の落下方向分割エリアの表示位置を左右STで切り替える処理
  左STの機器設定を表示する場合、落下方向の"くわしい"設定の左側には「エリア1」の落下方向を、右側には「エリア2」の落下方向が表示されるように変数値をセットする。
  右STの機器設定を表示する場合、落下方向の"くわしい"設定の左側には「エリア2」の落下方向を、右側には「エリア1」の落下方向が表示されるように変数値をセットする。
  */
  // 左側の分割エリア表示用配列作成
  const detailGiftFallingDirectionLeftside = isLeft ? pitConfDuo?.detailArea1GiftFallingDirection : pitConfDuo?.detailArea2GiftFallingDirection;
  // 右側の分割エリア表示用配列作成
  const detailGiftFallingDirectionRightside = isLeft ? pitConfDuo?.detailArea2GiftFallingDirection : pitConfDuo?.detailArea1GiftFallingDirection;



  return (
    <Box className="contentArea" style={{ height: '508px' }}>
      <Table className={classes.table} aria-label="table">
        <TableHead
          sx={{
            backgroundColor: '#B3B3B3',
            '& .MuiTableCell-head': {
              fontSize: '19px',
            },
          }}>
          <TableRow>
            <TableCell
              colSpan={2}
              sx={{
                width: headerWidth[0],
              }}>
              設定項目
            </TableCell>
            <TableCell
              colSpan={2}
              sx={{
                width: headerWidth[1],
              }}>
              かんたん
            </TableCell>
            <TableCell
              colSpan={4}
              sx={{
                width: headerWidth[2],
              }}>
              くわしい
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody
          sx={{
            '& .MuiTableCell-root:not(.bgGray)': {
              backgroundColor: '#FFFFFF',
            },
            '& .MuiTableCell-body': {
              fontSize: '15px',
            },
          }}>
          <TableRow>
            <TableCell
              className="bgGray"
              rowSpan={6}
              sx={{
                width: '100px',
                textAlign: 'center',
              }}>
              パワー
            </TableCell>

            <TableCell
              className="bgGray"
              rowSpan={3}
              sx={{
                width: '100px',
                textAlign: 'center',
              }}>
              上昇時
            </TableCell>

            <TableCell colSpan={2} rowSpan={3} sx={{ position: 'relative' }}>
              {areaConfDuo?.simpleLiftupPower}
              {areaConfDuo?.armPowerConfigType === COMMON.DUO_CONFIG_TYPE.DETAIL && !isPartialCheckActive && (
                <div className={classes.grayCover} style={{ width: '311px', height: '154px' }} />
              )}
              {areaConfDuo?.armPowerConfigType === COMMON.DUO_CONFIG_TYPE.DETAIL && isPartialCheckActive && (
                <div className={classes.grayCoverDark} style={{ width: '311px', height: '154px' }} />
              )}
            </TableCell>
            <TableCell
              className="bgGray"
              sx={{
                width: '100px',
                position: 'relative',
              }}>
              エリア
              {areaConfDuo?.armPowerConfigType === COMMON.DUO_CONFIG_TYPE.SIMPLE && !isPartialCheckActive && (
                <div className={classes.grayCover} style={{ width: '381px', height: '154px' }} />
              )}
              {areaConfDuo?.armPowerConfigType === COMMON.DUO_CONFIG_TYPE.SIMPLE && isPartialCheckActive && (
                <div className={classes.grayCoverDark} style={{ width: '381px', height: '154px' }} />
              )}
            </TableCell>
            <TableCell colSpan={3} sx={{}}>
              {areaConfDuo?.areaSplit &&
                getClassificationName(CLSFCN.DUO_AREA_SPLIT.CATEGORY_CD, areaConfDuo.areaSplit)}
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell
              className="bgGray"
              sx={{
                width: '100px',
              }}>
              境界線
            </TableCell>
            <TableCell colSpan={3} sx={{}}>
              <div style={{ display: 'flex', width: '128px' }}>
                横<div className={classes.marginX}>{areaConfDuo?.areaX}</div>縦
                <div className={classes.marginX}>{areaConfDuo?.areaY}</div>
              </div>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell colSpan={2} sx={{}}>
              <div style={{ display: 'flex', width: '128px' }}>
                {/* アームパワー上昇時 左側分割エリア表示 */}
                <div className={classes.marginX}>{detailLiftupLPowerLeftside}</div>/
                <div className={classes.marginX}>{detailLiftupCPowerLeftside}</div>/
                <div className={classes.marginX}>{detailLiftupRPowerLeftside}</div>
              </div>
            </TableCell>
            <TableCell colSpan={2} sx={{}}>
              <div style={{ display: 'flex', width: '128px' }}>
                {/* アームパワー上昇時 右側分割エリア表示 */}
                <div className={classes.marginX}>{detailLiftupLPowerRightside}</div>/
                <div className={classes.marginX}>{detailLiftupCPowerRightside}</div>/
                <div className={classes.marginX}>{detailLiftupRPowerRightside}</div>
              </div>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell
              className="bgGray"
              rowSpan={3}
              sx={{
                width: '100px',
                textAlign: 'center',
              }}>
              つかみ時
            </TableCell>

            <TableCell colSpan={2} rowSpan={3} sx={{}}>
              {areaConfDuo?.simpleGrabPower}
            </TableCell>
            <TableCell
              className="bgGray"
              sx={{
                width: '100px',
              }}>
              エリア
            </TableCell>
            <TableCell colSpan={3} sx={{}}>
              {areaConfDuo?.areaSplit &&
                getClassificationName(CLSFCN.DUO_AREA_SPLIT.CATEGORY_CD, areaConfDuo.areaSplit)}
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell
              className="bgGray"
              sx={{
                width: '100px',
              }}>
              境界線
            </TableCell>
            <TableCell colSpan={3} sx={{}}>
              <div style={{ display: 'flex', width: '128px' }}>
                横<div className={classes.marginX}>{areaConfDuo?.areaX}</div>縦
                <div className={classes.marginX}>{areaConfDuo?.areaY}</div>
              </div>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell colSpan={2} sx={{}}>
              <div style={{ display: 'flex', width: '128px' }}>
                {/* アームパワーつかみ時 左側分割エリア表示 */}
                <div className={classes.marginX}>{detailGrabLPowerLeftside}</div>/
                <div className={classes.marginX}>{detailGrabCPowerLeftside}</div>/
                <div className={classes.marginX}>{detailGrabRPowerLeftside}</div>
              </div>
            </TableCell>
            <TableCell colSpan={2} sx={{}}>
              <div style={{ display: 'flex', width: '128px' }}>
                {/* アームパワーつかみ時 右側分割エリア表示 */}
                <div className={classes.marginX}>{detailGrabLPowerRightside}</div>/
                <div className={classes.marginX}>{detailGrabCPowerRightside}</div>/
                <div className={classes.marginX}>{detailGrabRPowerRightside}</div>
              </div>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell
              colSpan={2}
              rowSpan={7}
              className="bgGray"
              sx={{
                textAlign: 'center',
              }}>
              タイミング
            </TableCell>
            <TableCell
              rowSpan={3}
              className="bgGray"
              sx={{ width: '154px', verticalAlign: 'bottom', position: 'relative' }}>
              横移動時
              {areaConfDuo?.timingConfigType === COMMON.DUO_CONFIG_TYPE.DETAIL && !isPartialCheckActive &&(
                <div className={classes.grayCover} style={{ width: '311px', height: '180px' }} />
              )}
              {areaConfDuo?.timingConfigType === COMMON.DUO_CONFIG_TYPE.DETAIL && isPartialCheckActive &&(
                <div className={classes.grayCoverDark} style={{ width: '311px', height: '180px' }} />
              )}
            </TableCell>
            <TableCell rowSpan={3} sx={{ verticalAlign: 'bottom' }}>
              {areaConfDuo?.simpleMoving && getClassificationName(CLSFCN.ON_OFF.CATEGORY_CD, areaConfDuo.simpleMoving)}
            </TableCell>
            <TableCell
              className="bgGray"
              sx={{
                width: '100px',
                position: 'relative',
              }}>
              エリア
              {areaConfDuo?.timingConfigType === COMMON.DUO_CONFIG_TYPE.SIMPLE && !isPartialCheckActive &&(
                <div className={classes.grayCover} style={{ width: '381px', height: '180px' }} />
              )}
              {areaConfDuo?.timingConfigType === COMMON.DUO_CONFIG_TYPE.SIMPLE && isPartialCheckActive &&(
                <div className={classes.grayCoverDark} style={{ width: '381px', height: '180px' }} />
              )}
            </TableCell>
            <TableCell colSpan={3} sx={{}}>
              {areaConfDuo?.areaSplit &&
                getClassificationName(CLSFCN.DUO_AREA_SPLIT.CATEGORY_CD, areaConfDuo.areaSplit)}
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell
              className="bgGray"
              sx={{
                width: '100px',
              }}>
              境界線
            </TableCell>
            <TableCell colSpan={3} sx={{}}>
              <div style={{ display: 'flex', width: '128px' }}>
                横<div className={classes.marginX}>{areaConfDuo?.areaX}</div>縦
                <div className={classes.marginX}>{areaConfDuo?.areaY}</div>
              </div>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell
              className="bgGray"
              sx={{
                width: '100px',
              }}>
              横移動時
            </TableCell>
            <TableCell sx={{}}>
              {/* タイミング 左側分割エリア－横移動時 */}
              {areaConfDuo?.detailArea1Moving &&
                getClassificationName(CLSFCN.ON_OFF.CATEGORY_CD, detailMovingLeftside ?? '')}
            </TableCell>
            <TableCell
              className="bgGray"
              sx={{
                width: '100px',
              }}>
              横移動時
            </TableCell>
            <TableCell sx={{}}>
              {/* タイミング 右側分割エリア－横移動時 */}
              {areaConfDuo?.detailArea2Moving &&
                getClassificationName(CLSFCN.ON_OFF.CATEGORY_CD, detailMovingRightside ?? '')}
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell className="bgGray" sx={{ width: '154px' }}>
              上昇中3
            </TableCell>
            <TableCell sx={{}}>
              {areaConfDuo?.simpleUp3 && getClassificationName(CLSFCN.ON_OFF.CATEGORY_CD, areaConfDuo.simpleUp3)}
            </TableCell>
            <TableCell className="bgGray" sx={{}}>
              上昇中3
            </TableCell>
            <TableCell sx={{}}>
              {/* タイミング 左側分割エリア－上昇中3 */}
              {areaConfDuo?.detailArea1Up3 &&
                getClassificationName(CLSFCN.ON_OFF.CATEGORY_CD, detailUp3Leftside ?? '')}
            </TableCell>
            <TableCell className="bgGray" sx={{}}>
              上昇中3
            </TableCell>
            <TableCell sx={{}}>
              {/* タイミング 右側分割エリア－上昇中3 */}
              {areaConfDuo?.detailArea2Up3 &&
                getClassificationName(CLSFCN.ON_OFF.CATEGORY_CD, detailUp3Rightside ?? '')}
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell className="bgGray" sx={{ width: '154px' }}>
              上昇中2
            </TableCell>
            <TableCell sx={{}}>
              {areaConfDuo?.simpleUp2 && getClassificationName(CLSFCN.ON_OFF.CATEGORY_CD, areaConfDuo.simpleUp2)}
            </TableCell>
            <TableCell className="bgGray" sx={{}}>
              上昇中2
            </TableCell>
            <TableCell sx={{}}>
              {/* タイミング 左側分割エリア－上昇中2 */}
              {areaConfDuo?.detailArea1Up2 &&
                getClassificationName(CLSFCN.ON_OFF.CATEGORY_CD, detailUp2Leftside ?? '')}
            </TableCell>
            <TableCell className="bgGray" sx={{}}>
              上昇中2
            </TableCell>
            <TableCell sx={{}}>
              {/* タイミング 右側分割エリア－上昇中2 */}
              {areaConfDuo?.detailArea2Up2 &&
                getClassificationName(CLSFCN.ON_OFF.CATEGORY_CD, detailUp2Rightside ?? '')}
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell className="bgGray" sx={{ width: '154px' }}>
              上昇中1
            </TableCell>
            <TableCell sx={{}}>
              {areaConfDuo?.simpleUp1 && getClassificationName(CLSFCN.ON_OFF.CATEGORY_CD, areaConfDuo.simpleUp1)}
            </TableCell>
            <TableCell className="bgGray" sx={{}}>
              上昇中1
            </TableCell>
            <TableCell sx={{}}>
              {/* タイミング 左側分割エリア－上昇中1 */}
              {areaConfDuo?.detailArea1Up1 &&
                getClassificationName(CLSFCN.ON_OFF.CATEGORY_CD, detailUp1Leftside ?? '')}
            </TableCell>
            <TableCell className="bgGray" sx={{}}>
              上昇中1
            </TableCell>
            <TableCell sx={{}}>
              {/* タイミング 右側分割エリア－上昇中1 */}
              {areaConfDuo?.detailArea2Up1 &&
                getClassificationName(CLSFCN.ON_OFF.CATEGORY_CD, detailUp1Rightside ?? '')}
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell className="bgGray" sx={{ width: '154px' }}>
              上昇直後
            </TableCell>
            <TableCell sx={{}}>
              {areaConfDuo?.simpleImmediate &&
                getClassificationName(CLSFCN.ON_OFF.CATEGORY_CD, areaConfDuo.simpleImmediate)}
            </TableCell>
            <TableCell className="bgGray" sx={{}}>
              上昇直後
            </TableCell>
            <TableCell sx={{}}>
              {/* タイミング 左側分割エリア－上昇直後 */}
              {areaConfDuo?.detailArea1Immediate &&
                getClassificationName(CLSFCN.ON_OFF.CATEGORY_CD, detailImmediateLeftside ?? '')}
            </TableCell>
            <TableCell className="bgGray" sx={{}}>
              上昇直後
            </TableCell>
            <TableCell sx={{}}>
              {/* タイミング 右側分割エリア－上昇直後 */}
              {areaConfDuo?.detailArea2Immediate &&
                getClassificationName(CLSFCN.ON_OFF.CATEGORY_CD, detailImmediateRightside ?? '')}
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell
              colSpan={2}
              rowSpan={3}
              className="bgGray"
              sx={{
                textAlign: 'center',
              }}>
              落下方向
            </TableCell>
            <TableCell colSpan={2} rowSpan={3} sx={{ justifyItems: 'center', position: 'relative' }}>
              <Box className={classes.fallingDirection}>
                <div style={{ display: 'flex' }}>
                  <div className={`arrow ${pitConfDuo?.simpleGiftFallingDirection?.[7] === '0' ? 'colorGray' : ''}`}>
                    ↖
                  </div>
                  <div className={`arrow ${pitConfDuo?.simpleGiftFallingDirection?.[0] === '0' ? 'colorGray' : ''}`}>
                    ↑
                  </div>
                  <div className={`arrow ${pitConfDuo?.simpleGiftFallingDirection?.[1] === '0' ? 'colorGray' : ''}`}>
                    ↗
                  </div>
                </div>
                <div style={{ display: 'flex' }}>
                  <div className={`arrow ${pitConfDuo?.simpleGiftFallingDirection?.[6] === '0' ? 'colorGray' : ''}`}>
                    ←
                  </div>
                  <div className={`arrow ${pitConfDuo?.simpleGiftFallingDirection?.[5] === '0' ? 'colorGray' : ''}`}>
                    ↙
                  </div>
                  <div className={`arrow ${pitConfDuo?.simpleGiftFallingDirection?.[4] === '0' ? 'colorGray' : ''}`}>
                    ↓
                  </div>
                  <div className={`arrow ${pitConfDuo?.simpleGiftFallingDirection?.[3] === '0' ? 'colorGray' : ''}`}>
                    ↘
                  </div>
                  <div className={`arrow ${pitConfDuo?.simpleGiftFallingDirection?.[2] === '0' ? 'colorGray' : ''}`}>
                    →
                  </div>
                </div>
                <div style={{ display: 'flex' }}>
                  <div className={`arrow ${pitConfDuo?.simpleGiftFallingDirection?.[8] === '0' ? 'colorGray' : ''}`}>
                    ↓
                  </div>
                </div>
              </Box>
              {pitConfDuo?.dropDirectionConfigType === COMMON.DUO_CONFIG_TYPE.DETAIL && !isPartialCheckActive && (
                <div className={classes.grayCover} style={{ width: '311px', height: '145px' }} />
              )}
              {pitConfDuo?.dropDirectionConfigType === COMMON.DUO_CONFIG_TYPE.DETAIL && isPartialCheckActive && (
                <div className={classes.grayCoverDark} style={{ width: '311px', height: '145px' }} />
              )}
            </TableCell>
            <TableCell className="bgGray" sx={{ position: 'relative' }}>
              エリア
              {pitConfDuo?.dropDirectionConfigType === COMMON.DUO_CONFIG_TYPE.SIMPLE && !isPartialCheckActive && (
                <div className={classes.grayCover} style={{ width: '381px', height: '145px' }} />
              )}
              {pitConfDuo?.dropDirectionConfigType === COMMON.DUO_CONFIG_TYPE.SIMPLE && isPartialCheckActive && (
                <div className={classes.grayCoverDark} style={{ width: '381px', height: '145px' }} />
              )}
            </TableCell>
            <TableCell colSpan={3} sx={{}}>
              {areaConfDuo?.areaSplit &&
                getClassificationName(CLSFCN.DUO_AREA_SPLIT.CATEGORY_CD, areaConfDuo.areaSplit)}
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell className="bgGray" sx={{}}>
              境界線
            </TableCell>
            <TableCell colSpan={3} sx={{}}>
              <div style={{ display: 'flex', width: '128px' }}>
                横<div className={classes.marginX}>{areaConfDuo?.areaX}</div>縦
                <div className={classes.marginX}>{areaConfDuo?.areaY}</div>
              </div>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell colSpan={2} sx={{ height: '94px', justifyItems: 'center' }}>
              <Box className={classes.fallingDirection}>
                <div style={{ display: 'flex' }}>
                  {/* 落下方向 くわしい 左側分割エリア－左斜め上 */}
                  <div
                    className={`arrow ${detailGiftFallingDirectionLeftside?.[7] !== '1' ? 'colorGray' : ''}`}>
                    ↖
                  </div>
                  {/* 落下方向 くわしい 左側分割エリア－上 */}
                  <div
                    className={`arrow ${detailGiftFallingDirectionLeftside?.[0] !== '1' ? 'colorGray' : ''}`}>
                    ↑
                  </div>
                  {/* 落下方向 くわしい 左側分割エリア－右斜め上 */}
                  <div
                    className={`arrow ${detailGiftFallingDirectionLeftside?.[1] !== '1' ? 'colorGray' : ''}`}>
                    ↗
                  </div>
                </div>
                <div style={{ display: 'flex' }}>
                  {/* 落下方向 くわしい 左側分割エリア－左 */}
                  <div
                    className={`arrow ${detailGiftFallingDirectionLeftside?.[6] !== '1' ? 'colorGray' : ''}`}>
                    ←
                  </div>
                  {/* 落下方向 くわしい 左側分割エリア－左斜め下 */}
                  <div
                    className={`arrow ${detailGiftFallingDirectionLeftside?.[5] !== '1' ? 'colorGray' : ''}`}>
                    ↙
                  </div>
                  {/* 落下方向 くわしい 左側分割エリア－下 */}
                  <div
                    className={`arrow ${detailGiftFallingDirectionLeftside?.[4] !== '1' ? 'colorGray' : ''}`}>
                    ↓
                  </div>
                  {/* 落下方向 くわしい 左側分割エリア－右斜め下 */}
                  <div
                    className={`arrow ${detailGiftFallingDirectionLeftside?.[3] !== '1' ? 'colorGray' : ''}`}>
                    ↘
                  </div>
                  {/* 落下方向 くわしい 左側分割エリア－右 */}
                  <div
                    className={`arrow ${detailGiftFallingDirectionLeftside?.[2] !== '1' ? 'colorGray' : ''}`}>
                    →
                  </div>
                </div>
                <div style={{ display: 'flex' }}>
                  {/* 落下方向 くわしい 左側分割エリア－真下 */}
                  <div
                    className={`arrow ${detailGiftFallingDirectionLeftside?.[8] !== '1' ? 'colorGray' : ''}`}>
                    ↓
                  </div>
                </div>
              </Box>
            </TableCell>
            <TableCell colSpan={2} sx={{ justifyItems: 'center' }}>
              <Box className={classes.fallingDirection}>
                <div style={{ display: 'flex' }}>
                  {/* 落下方向 くわしい  右側分割エリア－左斜め上 */}
                  <div
                    className={`arrow ${detailGiftFallingDirectionRightside?.[7] !== '1' ? 'colorGray' : ''}`}>
                    ↖
                  </div>
                  {/* 落下方向 くわしい  右側分割エリア－上 */}
                  <div
                    className={`arrow ${detailGiftFallingDirectionRightside?.[0] !== '1' ? 'colorGray' : ''}`}>
                    ↑
                  </div>
                  {/* 落下方向 くわしい  右側分割エリア－右斜め上 */}
                  <div
                    className={`arrow ${detailGiftFallingDirectionRightside?.[1] !== '1' ? 'colorGray' : ''}`}>
                    ↗
                  </div>
                </div>
                <div style={{ display: 'flex' }}>
                  {/* 落下方向 くわしい  右側分割エリア－左 */}
                  <div
                    className={`arrow ${detailGiftFallingDirectionRightside?.[6] !== '1' ? 'colorGray' : ''}`}>
                    ←
                  </div>
                  {/* 落下方向 くわしい  右側分割エリア－左斜め下 */}
                  <div
                    className={`arrow ${detailGiftFallingDirectionRightside?.[5] !== '1' ? 'colorGray' : ''}`}>
                    ↙
                  </div>
                  {/* 落下方向 くわしい  右側分割エリア－下 */}
                  <div
                    className={`arrow ${detailGiftFallingDirectionRightside?.[4] !== '1' ? 'colorGray' : ''}`}>
                    ↓
                  </div>
                  {/* 落下方向 くわしい  右側分割エリア－右斜め下 */}
                  <div
                    className={`arrow ${detailGiftFallingDirectionRightside?.[3] !== '1' ? 'colorGray' : ''}`}>
                    ↘
                  </div>
                  {/* 落下方向 くわしい  右側分割エリア－右 */}
                  <div
                    className={`arrow ${detailGiftFallingDirectionRightside?.[2] !== '1' ? 'colorGray' : ''}`}>
                    →
                  </div>
                </div>
                <div style={{ display: 'flex' }}>
                  {/* 落下方向 くわしい  右側分割エリア－真下 */}
                  <div
                    className={`arrow ${detailGiftFallingDirectionRightside?.[8] !== '1' ? 'colorGray' : ''}`}>
                    ↓
                  </div>
                </div>
              </Box>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
};

const TablePage2: React.FC<React.PropsWithChildren<TablePageProps>> = ({ machineDuoConf, isPartialCheckActive }) => {
  const { classes } = useStyles();
  const { operationConfDuo, areaConfDuo, pitConfDuo, partitionOtherConfDuo, ledSoundConfDuo, conf } = machineDuoConf;
  const headerWidth = ['224px', '312px', '382px'];
  // ラベル用 上昇・下降
  const labelUpdown = partitionOtherConfDuo?.detailLinkedUpdown
    ? getClassificationName(CLSFCN.DUO_UP_DOWN.CATEGORY_CD, partitionOtherConfDuo.detailLinkedUpdown)
    : '-';
  return (
    <Box className="contentArea" style={{ height: '490px' }}>
      <Table className={classes.table} aria-label="table">
        <TableHead
          sx={{
            backgroundColor: '#B3B3B3',
            '& .MuiTableCell-head': {
              fontSize: '19px',
            },
          }}>
          <TableRow>
            <TableCell
              colSpan={2}
              sx={{
                width: headerWidth[0],
              }}>
              設定項目
            </TableCell>
            <TableCell
              colSpan={2}
              sx={{
                width: headerWidth[1],
              }}>
              かんたん
            </TableCell>
            <TableCell
              colSpan={2}
              sx={{
                width: headerWidth[2],
              }}>
              くわしい
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody
          sx={{
            '& .MuiTableCell-root:not(.bgGray)': {
              backgroundColor: '#FFFFFF',
            },
            '& .MuiTableCell-body': {
              fontSize: '15px',
            },
          }}>
          <TableRow>
            <TableCell
              className="bgGray"
              rowSpan={6}
              sx={{
                width: '108px',
                textAlign: 'center',
              }}>
              移動範囲
            </TableCell>

            <TableCell
              className="bgGray"
              rowSpan={3}
              sx={{
                textAlign: 'center',
              }}>
              キャッチャー
              <br />
              移動範囲
            </TableCell>

            <TableCell
              className="bgGray"
              rowSpan={3}
              sx={{
                width: '118px',
                position: 'relative',
              }}>
              E
              {areaConfDuo?.armMoveConfigType === COMMON.DUO_CONFIG_TYPE.DETAIL && !isPartialCheckActive && (
                <div className={classes.grayCover} style={{ width: '311px', height: '146px' }} />
              )}
              {areaConfDuo?.armMoveConfigType === COMMON.DUO_CONFIG_TYPE.DETAIL && isPartialCheckActive && (
                <div className={classes.grayCoverDark} style={{ width: '311px', height: '146px' }} />
              )}
            </TableCell>

            <TableCell rowSpan={3} sx={{}}>
              {areaConfDuo?.simpleY}
            </TableCell>
            <TableCell
              className="bgGray"
              sx={{
                width: '220px',
                position: 'relative',
              }}>
              H
              {areaConfDuo?.armMoveConfigType === COMMON.DUO_CONFIG_TYPE.SIMPLE && !isPartialCheckActive && (
                <div className={classes.grayCover} style={{ width: headerWidth[2], height: '146px' }} />
              )}
              {areaConfDuo?.armMoveConfigType === COMMON.DUO_CONFIG_TYPE.SIMPLE && isPartialCheckActive && (
                <div className={classes.grayCoverDark} style={{ width: headerWidth[2], height: '146px' }} />
              )}
            </TableCell>
            <TableCell sx={{}}>
              <div style={{ display: 'flex' }}>
                <div>{areaConfDuo?.detailHx}</div>,<div className={classes.marginX}>{areaConfDuo?.detailHy}</div>
              </div>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell className="bgGray" sx={{}}>
              E
            </TableCell>
            <TableCell sx={{}}>
              <div style={{ display: 'flex' }}>
                <div>{areaConfDuo?.detailEx}</div>,<div className={classes.marginX}>{areaConfDuo?.detailEy}</div>
              </div>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell className="bgGray" sx={{}}>
              HP
            </TableCell>
            <TableCell sx={{}}>
              <div style={{ display: 'flex' }}>
                <div>{areaConfDuo?.detailHpX}</div>,<div className={classes.marginX}>{areaConfDuo?.detailHpY}</div>
              </div>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell
              className="bgGray"
              sx={{
                textAlign: 'center',
              }}>
              初期高さ
            </TableCell>

            <TableCell
              colSpan={2}
              sx={{
                textAlign: 'center',
              }}>
              -
            </TableCell>
            <TableCell colSpan={2} sx={{}}>
              {areaConfDuo?.detailInitHeight}
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell
              className="bgGray"
              sx={{
                textAlign: 'center',
              }}>
              下降限界
            </TableCell>

            <TableCell colSpan={2} sx={{}}>
              {areaConfDuo?.simpleDescentLimit}
            </TableCell>
            <TableCell colSpan={2} sx={{}}>
              {areaConfDuo?.detailDescentLimit}
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell
              className="bgGray"
              sx={{
                textAlign: 'center',
              }}>
              アームセンサ
            </TableCell>

            <TableCell
              colSpan={2}
              sx={{
                textAlign: 'center',
              }}>
              -
            </TableCell>
            <TableCell colSpan={2} sx={{}}>
              {areaConfDuo?.detailArmSizeSync &&
                getClassificationName(CLSFCN.ON_OFF.CATEGORY_CD, areaConfDuo.detailArmSizeSync)}
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell
              className="bgGray"
              colSpan={2}
              rowSpan={4}
              sx={{
                textAlign: 'center',
              }}>
              落とし穴
            </TableCell>

            <TableCell colSpan={2} rowSpan={4} sx={{ position: 'relative' }}>
              {pitConfDuo?.simplePitPosition &&
                getClassificationName(CLSFCN.DUO_PIT_POSITION.CATEGORY_CD, pitConfDuo.simplePitPosition)}
              {pitConfDuo?.pitConfigType === COMMON.DUO_CONFIG_TYPE.DETAIL && !isPartialCheckActive && (
                <div className={classes.grayCover} style={{ width: '311px', height: '96px' }} />
              )}
              {pitConfDuo?.pitConfigType === COMMON.DUO_CONFIG_TYPE.DETAIL && isPartialCheckActive && (
                <div className={classes.grayCoverDark} style={{ width: '311px', height: '96px' }} />
              )}
            </TableCell>
            <TableCell className="bgGray" sx={{ position: 'relative' }}>
              落とし穴1
              {pitConfDuo?.pitConfigType === COMMON.DUO_CONFIG_TYPE.SIMPLE && !isPartialCheckActive && (
                <div className={classes.grayCover} style={{ width: headerWidth[2], height: '96px' }} />
              )}
              {pitConfDuo?.pitConfigType === COMMON.DUO_CONFIG_TYPE.SIMPLE && isPartialCheckActive && (
                <div className={classes.grayCoverDark} style={{ width: headerWidth[2], height: '96px' }} />
              )}
            </TableCell>
            <TableCell sx={{}}>
              <div style={{ display: 'flex' }}>
                <div>
                  {pitConfDuo?.detailPit1Conf &&
                    getClassificationName(CLSFCN.DUO_PIT_CONF.CATEGORY_CD, pitConfDuo.detailPit1Conf)}
                </div>
                <div className={classes.marginLeft}>
                  {pitConfDuo?.detailPit1Conf === CLSFCN.DUO_PIT_CONF.VALID ? pitConfDuo?.detailPit1x : '---'}
                </div>
                ,
                <div className={classes.marginX}>
                  {pitConfDuo?.detailPit1Conf === CLSFCN.DUO_PIT_CONF.VALID ? pitConfDuo?.detailPit1y : '---'}
                </div>
              </div>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell className="bgGray" sx={{}}>
              落とし穴2
            </TableCell>
            <TableCell sx={{}}>
              <div style={{ display: 'flex' }}>
                <div>
                  {pitConfDuo?.detailPit2Conf &&
                    getClassificationName(CLSFCN.DUO_PIT_CONF.CATEGORY_CD, pitConfDuo.detailPit2Conf)}
                </div>
                <div className={classes.marginLeft}>
                  {pitConfDuo?.detailPit2Conf === CLSFCN.DUO_PIT_CONF.VALID ? pitConfDuo?.detailPit2x : '---'}
                </div>
                ,
                <div className={classes.marginX}>
                  {pitConfDuo?.detailPit2Conf === CLSFCN.DUO_PIT_CONF.VALID ? pitConfDuo?.detailPit2y : '---'}
                </div>
              </div>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell className="bgGray" sx={{}}>
              落とし穴3
            </TableCell>
            <TableCell sx={{}}>
              <div style={{ display: 'flex' }}>
                <div>
                  {pitConfDuo?.detailPit3Conf &&
                    getClassificationName(CLSFCN.DUO_PIT_CONF.CATEGORY_CD, pitConfDuo.detailPit3Conf)}
                </div>
                <div className={classes.marginLeft}>
                  {pitConfDuo?.detailPit3Conf === CLSFCN.DUO_PIT_CONF.VALID ? pitConfDuo?.detailPit3x : '---'}
                </div>
                ,
                <div className={classes.marginX}>
                  {pitConfDuo?.detailPit3Conf === CLSFCN.DUO_PIT_CONF.VALID ? pitConfDuo?.detailPit3y : '---'}
                </div>
              </div>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell className="bgGray" sx={{}}>
              落とし穴4
            </TableCell>
            <TableCell sx={{}}>
              <div style={{ display: 'flex' }}>
                <div>
                  {pitConfDuo?.detailPit4Conf &&
                    getClassificationName(CLSFCN.DUO_PIT_CONF.CATEGORY_CD, pitConfDuo.detailPit4Conf)}
                </div>
                <div className={classes.marginLeft}>
                  {pitConfDuo?.detailPit4Conf === CLSFCN.DUO_PIT_CONF.VALID ? pitConfDuo?.detailPit4x : '---'}
                </div>
                ,
                <div className={classes.marginX}>
                  {pitConfDuo?.detailPit4Conf === CLSFCN.DUO_PIT_CONF.VALID ? pitConfDuo?.detailPit4y : '---'}
                </div>
              </div>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell
              colSpan={2}
              rowSpan={9}
              className="bgGray"
              sx={{
                textAlign: 'center',
              }}>
              サポートパーテーション
            </TableCell>
            <TableCell className="bgGray" sx={{ position: 'relative' }}>
              稼働
              {partitionOtherConfDuo?.partitionConfigType === COMMON.DUO_CONFIG_TYPE.DETAIL && !isPartialCheckActive && (
                <div className={classes.grayCover} style={{ width: '311px', height: '219px' }} />
              )}
              {partitionOtherConfDuo?.partitionConfigType === COMMON.DUO_CONFIG_TYPE.DETAIL && isPartialCheckActive && (
                <div className={classes.grayCoverDark} style={{ width: '311px', height: '219px' }} />
              )}
            </TableCell>
            <TableCell sx={{}}>
              {partitionOtherConfDuo?.partitionSupport &&
                getClassificationName(CLSFCN.ON_OFF.CATEGORY_CD, partitionOtherConfDuo.partitionSupport)}
            </TableCell>
            <TableCell className="bgGray" sx={{ position: 'relative' }}>
              稼働
              {partitionOtherConfDuo?.partitionConfigType === COMMON.DUO_CONFIG_TYPE.SIMPLE && !isPartialCheckActive && (
                <div className={classes.grayCover} style={{ width: headerWidth[2], height: '219px' }} />
              )}
              {partitionOtherConfDuo?.partitionConfigType === COMMON.DUO_CONFIG_TYPE.SIMPLE && isPartialCheckActive && (
                <div className={classes.grayCoverDark} style={{ width: headerWidth[2], height: '219px' }} />
              )}
            </TableCell>
            <TableCell sx={{}}>
              {partitionOtherConfDuo?.partitionSupport &&
                getClassificationName(CLSFCN.ON_OFF.CATEGORY_CD, partitionOtherConfDuo.partitionSupport)}
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell className="bgGray" sx={{}}>
              下降イメージ
            </TableCell>
            <TableCell sx={{}}>
              {partitionOtherConfDuo?.simpleLoweringType &&
                getClassificationName(CLSFCN.DUO_LOWERING_TYPE.CATEGORY_CD, partitionOtherConfDuo.simpleLoweringType)}
            </TableCell>
            <TableCell className="bgGray" sx={{}}>
              上下設定
            </TableCell>
            <TableCell sx={{}}>
              {partitionOtherConfDuo?.detailLinkedUpdown &&
                getClassificationName(CLSFCN.DUO_UP_DOWN.CATEGORY_CD, partitionOtherConfDuo.detailLinkedUpdown)}
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell className="bgGray" sx={{}}>
              高さ最大
            </TableCell>
            <TableCell sx={{}}>{partitionOtherConfDuo?.simpleUpperLimit}</TableCell>
            <TableCell className="bgGray" sx={{}}>
              高さ最大
            </TableCell>
            <TableCell sx={{}}>{partitionOtherConfDuo?.detailUpperLimit}</TableCell>
          </TableRow>

          <TableRow>
            <TableCell rowSpan={6} className="bgGray" sx={{ verticalAlign: 'top' }}>
              高さ最小
            </TableCell>
            <TableCell rowSpan={6} sx={{ verticalAlign: 'top' }}>
              {partitionOtherConfDuo?.simpleLowerLimit}
            </TableCell>
            <TableCell className="bgGray" sx={{}}>
              高さ最小
            </TableCell>
            <TableCell sx={{}}>{partitionOtherConfDuo?.detailLowerLimit}</TableCell>
          </TableRow>

          <TableRow>
            <TableCell className="bgGray" sx={{}}>
              連続プレイ終了時リセット
            </TableCell>
            <TableCell sx={{}}>
              {partitionOtherConfDuo?.detailResetSetting &&
                getClassificationName(CLSFCN.ON_OFF.CATEGORY_CD, partitionOtherConfDuo.detailResetSetting)}
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell className="bgGray" sx={{}}>
              初回動作:連続プレイ
            </TableCell>
            <TableCell sx={{}}>{partitionOtherConfDuo?.detailInitPlaycount}</TableCell>
          </TableRow>

          <TableRow>
            <TableCell className="bgGray" sx={{}}>
              初回動作:{labelUpdown}
            </TableCell>
            <TableCell sx={{}}>{partitionOtherConfDuo?.detailInitMovement}</TableCell>
          </TableRow>

          <TableRow>
            <TableCell className="bgGray" sx={{}}>
              2回目以降:連続プレイ
            </TableCell>
            <TableCell sx={{}}>{partitionOtherConfDuo?.detailSecondPlaycount}</TableCell>
          </TableRow>

          <TableRow>
            <TableCell className="bgGray" sx={{}}>
              2回目以降:{labelUpdown}
            </TableCell>
            <TableCell sx={{}}>{partitionOtherConfDuo?.detailSecondMovement}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
};

const TablePage3: React.FC<React.PropsWithChildren<TablePageProps>> = ({ machineDuoConf, isPartialCheckActive }) => {
  const { classes } = useStyles();
  const { operationConfDuo, areaConfDuo, pitConfDuo, partitionOtherConfDuo, ledSoundConfDuo, conf } = machineDuoConf;
  const headerWidth = ['224px', '312px', '382px'];
  return (
    <Box className="contentArea" style={{ height: '360px' }}>
      <Table className={classes.table} aria-label="table">
        <TableHead
          sx={{
            backgroundColor: '#B3B3B3',
            '& .MuiTableCell-head': {
              fontSize: '19px',
            },
          }}>
          <TableRow>
            <TableCell
              colSpan={2}
              sx={{
                width: headerWidth[0],
              }}>
              設定項目
            </TableCell>
            <TableCell
              colSpan={2}
              sx={{
                width: headerWidth[1],
              }}>
              かんたん
            </TableCell>
            <TableCell
              colSpan={2}
              sx={{
                width: headerWidth[2],
              }}>
              くわしい
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody
          sx={{
            '& .MuiTableCell-root:not(.bgGray)': {
              backgroundColor: '#FFFFFF',
            },
            '& .MuiTableCell-body': {
              fontSize: '15px',
            },
          }}>
          <TableRow>
            <TableCell
              className="bgGray"
              rowSpan={10}
              sx={{
                width: '108px',
                textAlign: 'center',
              }}>
              サポート
              <br />
              運営
            </TableCell>

            <TableCell
              className="bgGray"
              rowSpan={5}
              sx={{
                textAlign: 'center',
              }}>
              獲得サポート
            </TableCell>
            <TableCell
              className="bgGray"
              sx={{
                width: '160px',
                position: 'relative',
              }}>
              サポート内容
              {operationConfDuo?.supportSetting !== COMMON.DUO_SUPPORT_SETTING.GET_SUPPORT && !isPartialCheckActive && (
                <div className={classes.grayCover} style={{ width: '310px', height: '131px' }} />
              )}
              {operationConfDuo?.supportSetting !== COMMON.DUO_SUPPORT_SETTING.GET_SUPPORT && isPartialCheckActive && (
                <div className={classes.grayCoverDark} style={{ width: '310px', height: '131px' }} />
              )}
              {operationConfDuo?.supportSetting === COMMON.DUO_SUPPORT_SETTING.GET_SUPPORT && isPartialCheckActive && (
                <div className={classes.grayCoverDark} style={{ width: '310px', height: '78px' }} />
              )}
            </TableCell>
            <TableCell sx={{}}>
              {operationConfDuo?.converge &&
                getClassificationName(CLSFCN.DUO_CONVERGE.CATEGORY_CD, operationConfDuo.converge)}
            </TableCell>
            <TableCell
              colSpan={2}
              rowSpan={5}
              sx={{
                textAlign: 'center',
                position: 'relative',
              }}>
              -
              {!isPartialCheckActive && (<div className={classes.grayCover} style={{ width: '100%', height: '100%' }} />)}
              {isPartialCheckActive && (<div className={classes.grayCoverDark} style={{ width: '100%', height: '100%' }} />)}
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell className="bgGray" sx={{}}>
              景品原価
            </TableCell>
            <TableCell sx={{}}>
              <div style={{ display: 'flex' }}>
                <div className={classes.marginRight}>{operationConfDuo?.giftCost.toLocaleString()}円</div>
              </div>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell className="bgGray" sx={{}}>
              目標原価率
            </TableCell>
            <TableCell sx={{}}>
              <div style={{ display: 'flex' }}>
                <div className={classes.marginRight}>{operationConfDuo?.targetCostRate}%</div>
              </div>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell className="bgGray" sx={{}}>
              連続プレイアシスト
            </TableCell>
            <TableCell sx={{}}>
              {operationConfDuo?.playAssist &&
                getClassificationName(CLSFCN.ON_OFF.CATEGORY_CD, operationConfDuo.playAssist)}
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell className="bgGray" sx={{}}>
              設定金額
            </TableCell>
            <TableCell sx={{}}>
              <div style={{ display: 'flex' }}>
                <div className={classes.marginRight}>{operationConfDuo?.playAssistCost.toLocaleString()}円</div>
              </div>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell
              className="bgGray"
              rowSpan={4}
              sx={{
                textAlign: 'center',
              }}>
              プレイ回数
              <br />
              サポート
            </TableCell>

            <TableCell
              className="bgGray"
              sx={{
                position: 'relative',
              }}>
              失敗回数
              {(operationConfDuo?.supportSetting !== COMMON.DUO_SUPPORT_SETTING.PLAY_COUNT_SUPPORT ||
                operationConfDuo?.playSupportConfigType === COMMON.DUO_CONFIG_TYPE.DETAIL) && !isPartialCheckActive && (
                <div className={classes.grayCover} style={{ width: '310px', height: '151px' }} />
              )}
              {(operationConfDuo?.supportSetting !== COMMON.DUO_SUPPORT_SETTING.PLAY_COUNT_SUPPORT ||
                operationConfDuo?.playSupportConfigType === COMMON.DUO_CONFIG_TYPE.DETAIL) && isPartialCheckActive &&(
                <div className={classes.grayCoverDark} style={{ width: '310px', height: '151px' }} />
              )}
            </TableCell>
            <TableCell sx={{}}>
              <div style={{ display: 'flex' }}>
                <div className={classes.marginRight}>{operationConfDuo?.simpleFailureCount}</div>回
              </div>
            </TableCell>
            <TableCell
              className="bgGray"
              sx={{
                width: '174px',
                position: 'relative',
              }}>
              失敗回数
              {(operationConfDuo?.supportSetting !== COMMON.DUO_SUPPORT_SETTING.PLAY_COUNT_SUPPORT ||
                operationConfDuo?.playSupportConfigType === COMMON.DUO_CONFIG_TYPE.SIMPLE) && !isPartialCheckActive && (
                <div className={classes.grayCover} style={{ width: headerWidth[2], height: '151px' }} />
              )}
              {(operationConfDuo?.supportSetting !== COMMON.DUO_SUPPORT_SETTING.PLAY_COUNT_SUPPORT ||
                operationConfDuo?.playSupportConfigType === COMMON.DUO_CONFIG_TYPE.SIMPLE) && isPartialCheckActive && (
                <div className={classes.grayCoverDark} style={{ width: headerWidth[2], height: '151px' }} />
              )}
            </TableCell>
            <TableCell sx={{ width: '204px' }}>
              <div style={{ display: 'flex' }}>
                <div className={classes.marginRight}>{operationConfDuo?.detailFailureCount}</div>回
              </div>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell className="bgGray" rowSpan={3} sx={{ verticalAlign: 'top' }}>
              増加パワー
            </TableCell>
            <TableCell rowSpan={3} sx={{ verticalAlign: 'top' }}>
              <div style={{ display: 'flex' }}>
                <div className={classes.marginRight}>{operationConfDuo?.simpleGrabPower}</div>
              </div>
            </TableCell>
            <TableCell className="bgGray" sx={{}}>
              増加パワー
            </TableCell>
            <TableCell sx={{}}>
              <div style={{ display: 'flex' }}>
                <div className={classes.marginRight}>{operationConfDuo?.detailGrabLPower}</div>/
                <div className={classes.marginX}>{operationConfDuo?.detailGrabCPower}</div>/
                <div className={classes.marginX}>{operationConfDuo?.detailGrabRPower}</div>
              </div>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell className="bgGray" sx={{}}>
              発動開始タイミング
            </TableCell>
            <TableCell sx={{}}>
              <div>
                <span className={classes.marginRight}>{operationConfDuo?.detailStartingTimingCount}</span>
                回目の連続プレイから
              </div>
              <div>サポート開始</div>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell className="bgGray" sx={{}}>
              <div>連続プレイ終了時</div>
              <div>リセット</div>
            </TableCell>
            <TableCell sx={{}}>
              {operationConfDuo?.detailResetSetting &&
                getClassificationName(CLSFCN.ON_OFF.CATEGORY_CD, operationConfDuo.detailResetSetting)}
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell
              className="bgGray"
              rowSpan={5}
              sx={{
                textAlign: 'center',
              }}>
              OFF
              <br />
              （標準運営）
            </TableCell>
            <TableCell colSpan={2} sx={{ position: 'relative' }}>
              {operationConfDuo?.supportSetting &&
              [COMMON.DUO_SUPPORT_SETTING.GET_SUPPORT, COMMON.DUO_SUPPORT_SETTING.PLAY_COUNT_SUPPORT].includes(
                operationConfDuo.supportSetting,
              ) ? (
                <div
                  style={{
                    textAlign: 'center',
                  }}>
                  -
                  {!isPartialCheckActive && (<div className={classes.grayCover} style={{ width: '100%', height: '49px' }} />)}
                  {isPartialCheckActive && (<div className={classes.grayCoverDark} style={{ width: '100%', height: '49px' }} />)}
                </div>
              ) : (
                <div>選択中</div>
              )}
            </TableCell>
            <TableCell
              colSpan={2}
              sx={{
                textAlign: 'center',
                position: 'relative',
              }}>
              -
              {!isPartialCheckActive && (<div className={classes.grayCover} style={{ width: '100%', height: '100%' }} />)}
              {isPartialCheckActive && (<div className={classes.grayCoverDark} style={{ width: '100%', height: '100%' }} />)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
};

const TablePage4: React.FC<React.PropsWithChildren<TablePageProps>> = ({ machineDuoConf, isPartialCheckActive }) => {
  const { classes } = useStyles();
  const { operationConfDuo, areaConfDuo, pitConfDuo, partitionOtherConfDuo, ledSoundConfDuo, conf } = machineDuoConf;

  // 保存設定から遷移かつ全設定確認
  const isAllCheckActive = machineDuoConf?.conf?.confDiv === COMMON.CONF_DIV.SAVE_CONF && !isPartialCheckActive;

  // 食品期限年月日
  let foodExpDate = '-/-/-';
  if (
    partitionOtherConfDuo?.foodExpShow === CLSFCN.ON_OFF.ON &&
    partitionOtherConfDuo?.foodExpType &&
    partitionOtherConfDuo?.foodExpDate
  ) {
    foodExpDate = formatDate(new Date(partitionOtherConfDuo.foodExpDate), '/', false);
  }
  const foodExpDateArray = foodExpDate.split('/');
  return (
    <Box className="contentArea">
      <Box style={{ width: '276px' }}>
        <Box>
          <Table className={classes.table} aria-label="table">
            <TableHead
              sx={{
                backgroundColor: '#B3B3B3',
                '& .MuiTableCell-head': {
                  fontSize: '19px',
                },
              }}>
              <TableRow>
                <TableCell
                  sx={{
                    width: '120px',
                  }}>
                  設定項目
                </TableCell>
                <TableCell
                  sx={{
                    width: '155px',
                  }}>
                  内容
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody
              sx={{
                '& .MuiTableCell-root:not(.bgGray)': {
                  backgroundColor: '#FFFFFF',
                },
                '& .MuiTableCell-body': {
                  fontSize: '15px',
                },
              }}>
              <TableRow>
                <TableCell
                  className="bgGray"
                  sx={{
                    textAlign: 'center',
                  }}>
                  景品原価
                </TableCell>

                <TableCell sx={{ position: 'relative' }}>
                  <div style={{ display: 'flex' }}>
                    <div className={classes.marginRight}>{operationConfDuo?.giftCost.toLocaleString()}円</div>
                  </div>
                  {isPartialCheckActive && (
                    <div className={classes.grayCoverDark} style={{ width: '100%', height: '48px' }} />
                  )}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell
                  className="bgGray"
                  sx={{
                    textAlign: 'center',
                  }}>
                  目標原価率
                </TableCell>

                <TableCell sx={{}}>
                  <div style={{ display: 'flex' }}>
                    <div className={classes.marginRight}>{operationConfDuo?.targetCostRate}%</div>
                  </div>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
        <Box style={{ marginTop: '20px' }}>
          <Table className={classes.table} aria-label="table">
            <TableHead
              sx={{
                backgroundColor: '#B3B3B3',
                '& .MuiTableCell-head': {
                  fontSize: '19px',
                },
              }}>
              <TableRow>
                <TableCell
                  sx={{
                    width: '120px',
                  }}>
                  設定項目
                </TableCell>
                <TableCell
                  sx={{
                    width: '155px',
                  }}>
                  内容
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody
              sx={{
                '& .MuiTableCell-root:not(.bgGray)': {
                  backgroundColor: '#FFFFFF',
                },
                '& .MuiTableCell-body': {
                  fontSize: '15px',
                },
              }}>
              <TableRow>
                <TableCell
                  className="bgGray"
                  sx={{
                    textAlign: 'center',
                  }}>
                  操作方法
                </TableCell>

                <TableCell sx={{ position: 'relative' }}>
                  <div style={{ display: 'flex' }}>
                    <div className={classes.marginRight}>
                      {operationConfDuo?.operatingMethod && getClassificationName(CLSFCN.OPERATING_METHOD.CATEGORY_CD, operationConfDuo?.operatingMethod)}
                    </div>
                  </div>
                  {(isAllCheckActive || isPartialCheckActive) && (
                  <div className={classes.grayCoverDark} style={{ width: '100%', height: '72px' }} />
                  )}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell
                  className="bgGray"
                  sx={{
                    textAlign: 'center',
                  }}>
                  アームサイズ
                </TableCell>

                <TableCell sx={{}}>
                  <div style={{ display: 'flex' }}>
                    <div className={classes.marginRight}>
                      {conf?.leftArmSize && getClassificationName(CLSFCN.DUO_ARM_SIZE.CATEGORY_CD, conf?.leftArmSize)}
                    </div>/
                    <div className={classes.marginX}>
                      {conf?.centerArmSize && getClassificationName(CLSFCN.DUO_ARM_SIZE.CATEGORY_CD, conf?.centerArmSize)}
                    </div>/
                    <div className={classes.marginX}>
                      {conf?.rightArmSize && getClassificationName(CLSFCN.DUO_ARM_SIZE.CATEGORY_CD, conf?.rightArmSize)}
                    </div>
                  </div>
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell
                  className="bgGray"
                  sx={{
                    textAlign: 'center',
                  }}>
                  回転角度
                </TableCell>

                <TableCell sx={{}}>
                  <div style={{ display: 'flex' }}>
                    <div className={classes.marginRight}>
                      {conf?.catcherDirection && getClassificationName(CLSFCN.DUO_CATCHER_DIRECTION.CATEGORY_CD, conf?.catcherDirection)}
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      </Box>
      <Box style={{ width: '600px', marginLeft: '40px' }}>
        <Table className={classes.table} aria-label="table">
          <TableHead
            sx={{
              backgroundColor: '#B3B3B3',
              '& .MuiTableCell-head': {
                fontSize: '19px',
              },
            }}>
            <TableRow>
              <TableCell
                colSpan={2}
                sx={{
                  width: '358px',
                }}>
                設定項目
              </TableCell>
              <TableCell
                sx={{
                  width: '242px',
                }}>
                内容
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody
            sx={{
              '& .MuiTableCell-root:not(.bgGray)': {
                backgroundColor: '#FFFFFF',
              },
              '& .MuiTableCell-root': {
                fontSize: '15px',
              },
            }}>
            <TableRow>
              <TableCell
                className="bgGray"
                rowSpan={2}
                sx={{
                  width: '120spx',
                  textAlign: 'center',
                }}>
                アーム
              </TableCell>
              <TableCell className="bgGray" sx={{ width: '236px' }}>
                開閉動作
              </TableCell>
              <TableCell sx={{ width: '244px' }}>
                {operationConfDuo?.armOperation &&
                  getClassificationName(CLSFCN.DUO_ARM_OPERATION.CATEGORY_CD, operationConfDuo.armOperation)}
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell className="bgGray" sx={{}}>
                開き幅
              </TableCell>
              <TableCell sx={{}}> {operationConfDuo?.openWidth}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell
                className="bgGray"
                rowSpan={4}
                sx={{
                  textAlign: 'center',
                }}>
                ゲームルール
              </TableCell>
              <TableCell className="bgGray" rowSpan={2} sx={{ position: 'relative' }}>
                ルール
                {isPartialCheckActive && (
                  <div className={classes.grayCoverDark} style={{ width: '479px', height: '464px' }} />
                )}
              </TableCell>
              <TableCell sx={{}}>
                {operationConfDuo?.timeLimit &&
                  getClassificationName(CLSFCN.DUO_TIME_LIMIT.CATEGORY_CD, operationConfDuo.timeLimit)}
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell sx={{}}>
                {operationConfDuo?.ruleSetting &&
                  getClassificationName(CLSFCN.DUO_RULE_SETTING.CATEGORY_CD, operationConfDuo.ruleSetting)}
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell className="bgGray" sx={{}}>
                下降ストップ
              </TableCell>
              <TableCell sx={{}}>
                {operationConfDuo?.downStopSetting &&
                  getClassificationName(CLSFCN.ON_OFF.CATEGORY_CD, operationConfDuo.downStopSetting)}
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell className="bgGray" sx={{}}>
                連続プレイみなし時間
              </TableCell>
              <TableCell sx={{}}>
                {operationConfDuo?.playingDeemedTime &&
                  getClassificationName(CLSFCN.DUO_TIME_LIMIT.CATEGORY_CD, operationConfDuo.playingDeemedTime)}
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell
                className="bgGray"
                rowSpan={3}
                sx={{
                  textAlign: 'center',
                }}>
                コイン
              </TableCell>
              <TableCell className="bgGray" sx={{}}>
                料金
              </TableCell>
              <TableCell sx={{ position: 'relative' }}>
                {operationConfDuo?.pricing && <div>{getClassificationName(CLSFCN.DUO_PRICING.CATEGORY_CD, operationConfDuo.pricing)}</div>}
              </TableCell>
            </TableRow>

            <TableCell className="bgGray" sx={{}}>
              電子マネー
            </TableCell>
            <TableCell sx={{ position: 'relative' }}>
              {operationConfDuo?.electronicMoney && (
                <div>
                  <div>1コイン：{operationConfDuo?.electronicMoney.toLocaleString()}円</div>
                </div>
              )}
              {!isPartialCheckActive && operationConfDuo?.feeSetting === '0' && (
                    <div className={classes.grayCover} style={{ width: '100%', height: '24px' }} />
                  )}
            </TableCell>
            <TableRow>
              <TableCell className="bgGray" sx={{}}>
                サービスボタン
              </TableCell>
              <TableCell sx={{}}>
                {operationConfDuo?.serviceButtonSetting &&
                  getClassificationName(
                    CLSFCN.DUO_SERVICE_BUTTON_SETTING.CATEGORY_CD,
                    operationConfDuo.serviceButtonSetting,
                  )}
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell
                className="bgGray"
                sx={{
                  textAlign: 'center',
                }}>
                景品センサ
              </TableCell>
              <TableCell className="bgGray" sx={{}}>
                見逃しサイズ
              </TableCell>
              <TableCell sx={{ position: 'relative' }}>
                {partitionOtherConfDuo?.prizeSensorSetting &&
                  getClassificationName(CLSFCN.DUO_PRIZE_SENSOR.CATEGORY_CD, partitionOtherConfDuo.prizeSensorSetting)}
                {isAllCheckActive && (
                  <div className={classes.grayCoverDark} style={{ width: '100%', height: '97px' }} />
                )}
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell
                className="bgGray"
                rowSpan={11}
                sx={{
                  textAlign: 'center',
                }}>
                その他
              </TableCell>
              <TableCell className="bgGray" sx={{}}>
                サポートパーテーション(左)
              </TableCell>
              <TableCell sx={{}}>
                {partitionOtherConfDuo?.partitionLeftPower
                  ? getClassificationName(CLSFCN.ON_OFF.CATEGORY_CD, partitionOtherConfDuo.partitionLeftPower)
                  : 'OFF'}
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell className="bgGray" sx={{}}>
                サポートパーテーション(右)
              </TableCell>
              <TableCell sx={{}}>
                {partitionOtherConfDuo?.partitionRightPower
                  ? getClassificationName(CLSFCN.ON_OFF.CATEGORY_CD, partitionOtherConfDuo.partitionRightPower)
                  : 'OFF'}
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell className="bgGray" sx={{}}>
                景品センサ
              </TableCell>
              <TableCell sx={{}}>
                {partitionOtherConfDuo?.prizeSensorPower
                  ? getClassificationName(CLSFCN.ON_OFF.CATEGORY_CD, partitionOtherConfDuo.prizeSensorPower)
                  : 'OFF'}
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell className="bgGray" sx={{}}>
                キャッチャー押し込み力
              </TableCell>
              <TableCell sx={{}}>
                {partitionOtherConfDuo?.pushingPower &&
                  getClassificationName(CLSFCN.DUO_PUSHING_POWER.CATEGORY_CD, partitionOtherConfDuo.pushingPower)}
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell className="bgGray" sx={{}}>
                キャッチャー速度(XY)
              </TableCell>
              <TableCell sx={{}}>
                {partitionOtherConfDuo?.catcherSpeedMove &&
                  getClassificationName(CLSFCN.CATCHER_SPEED.CATEGORY_CD, partitionOtherConfDuo.catcherSpeedMove)}
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell className="bgGray" sx={{}}>
                キャッチャー速度(Z)
              </TableCell>
              <TableCell sx={{}}>
                {partitionOtherConfDuo?.catcherSpeedDescent &&
                  getClassificationName(CLSFCN.CATCHER_SPEED.CATEGORY_CD, partitionOtherConfDuo.catcherSpeedDescent)}
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell className="bgGray" sx={{}}>
                食品の期限表示
              </TableCell>
              <TableCell sx={{ position: 'relative' }}>
                {partitionOtherConfDuo?.foodExpShow
                  ? getClassificationName(CLSFCN.ON_OFF.CATEGORY_CD, partitionOtherConfDuo.foodExpShow)
                  : 'OFF'}
                {isAllCheckActive && (
                  <div className={classes.grayCoverDark} style={{ width: '100%', height: '121px' }} />
                )}
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell className="bgGray" sx={{}}>
                期限の種類
              </TableCell>
              <TableCell sx={{}}>
                {partitionOtherConfDuo?.foodExpShow === CLSFCN.ON_OFF.ON && partitionOtherConfDuo?.foodExpType ? (
                  <div>
                    {getClassificationName(CLSFCN.EXPIRATION_TYPE.CATEGORY_CD, partitionOtherConfDuo.foodExpType)}
                  </div>
                ) : (
                  <div>-</div>
                )}
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell className="bgGray" sx={{}}>
                期限(年)
              </TableCell>
              <TableCell sx={{}}>
                {foodExpDateArray[0] === '-' ? (
                  <div>-</div>
                ) : (
                  <div style={{ display: 'flex' }}>
                    <div className={classes.marginRight}>{foodExpDateArray[0]}年</div>
                  </div>
                )}
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell className="bgGray" sx={{}}>
                期限(月)
              </TableCell>
              <TableCell sx={{}}>
                {foodExpDateArray[1] === '-' ? (
                  <div>-</div>
                ) : (
                  <div style={{ display: 'flex' }}>
                    <div className={classes.marginRight}>{foodExpDateArray[1]}月</div>
                  </div>
                )}
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell className="bgGray" sx={{}}>
                期限(日)
              </TableCell>
              <TableCell sx={{}}>
                {foodExpDateArray[2] === '-' ? (
                  <div>-</div>
                ) : (
                  <div style={{ display: 'flex' }}>
                    <div className={classes.marginRight}>{foodExpDateArray[2]}日</div>
                  </div>
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

export default StationHomeDetailDuo;
